import React, { isValidElement, useRef } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { times } from "lodash-es";
import { twJoin } from "tailwind-merge";

import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { useIsContentOverflows } from "src/utils/useIsContentOverflows";

export type PropertiesListItem = {
  key: string;
  label: string;
  icon?: IconProp | React.ReactElement;
  value: React.ReactNode;
};

type PropertiesListProps = {
  items: PropertiesListItem[];
  isLoading?: boolean;
  loadingRows?: number;
  showIcons?: boolean;
  dividers?: boolean;
  borders?: boolean;
  tooltips?: boolean;
  dataLocPrefix?: string;
  rightAlignValues?: boolean;
};

const LoadingRow: React.FC<{
  showIcons?: boolean;
  rightAlignValues?: boolean;
}> = ({ showIcons = false, rightAlignValues = false }) => {
  return (
    <div className="flex w-full">
      <div className="flex min-w-0 flex-1 items-center p-1.5 pl-0 font-inter-medium-12px">
        <div className="flex w-full items-center gap-0.5 truncate">
          {showIcons && <SkeletonPlaceholder height="h-3.5" width="w-3" />}
          <SkeletonPlaceholder height="h-3.5" width="w-20" />
        </div>
      </div>
      <div
        className={twJoin(
          "flex min-w-0 flex-1 p-1.5 font-inter-normal-12px text-gray-800",
          rightAlignValues && "justify-end",
        )}
      >
        <SkeletonPlaceholder height="h-3.5" width="w-32" />
      </div>
    </div>
  );
};

const PropertyIcon: React.FC<{ icon: IconProp | React.ReactElement }> = ({
  icon,
}) => {
  if (isValidElement(icon)) {
    return <div className="flex items-center">{icon}</div>;
  }

  return <Icon icon={icon as IconProp} size="2xs" />;
};

const PropertyLabel: React.FC<{
  label: string;
  icon?: IconProp | React.ReactElement;
  showIcon: boolean;
  enableTooltips: boolean;
}> = ({ label, icon, showIcon, enableTooltips }) => {
  const labelRef = useRef<HTMLDivElement>(null);
  const isOverflowing = useIsContentOverflows(labelRef);

  const labelContent = (
    <div ref={labelRef} className="flex w-full items-center gap-0.5 truncate">
      {showIcon &&
        (icon ? <PropertyIcon icon={icon} /> : <div className="w-3" />)}
      <span className="truncate">{label}</span>
    </div>
  );

  if (!enableTooltips || !isOverflowing) {
    return labelContent;
  }

  return (
    <Tooltip delay={200} disabled={!isOverflowing} title={label} asChild>
      {labelContent}
    </Tooltip>
  );
};

export const PropertiesList: React.FC<PropertiesListProps> = ({
  items,
  isLoading = false,
  loadingRows = 5,
  showIcons,
  dividers = true,
  tooltips = true,
  dataLocPrefix,
  rightAlignValues = false,
}) => {
  const shouldShowIcons =
    showIcons ?? (items.length > 0 && items.some((item) => !!item.icon));

  const locPrefix = dataLocPrefix || "property-list";

  return (
    <div
      className={twJoin("bg-white", dividers && "divide-y divide-gray-100")}
      data-loc={`${locPrefix}-container`}
    >
      {isLoading
        ? times(loadingRows, (index) => (
            <LoadingRow
              key={index}
              rightAlignValues={rightAlignValues}
              showIcons={shouldShowIcons}
            />
          ))
        : items.map(({ key, label, icon, value }) => (
            <div
              key={key}
              className="flex w-full"
              data-loc={`${locPrefix}-property-${key}`}
            >
              <div
                className="flex min-w-0 flex-1 items-start p-1.5 pl-0 font-inter-medium-12px"
                data-loc={`${locPrefix}-property-label-${key}`}
              >
                <PropertyLabel
                  enableTooltips={tooltips}
                  icon={icon}
                  label={label}
                  showIcon={shouldShowIcons}
                />
              </div>
              <div
                className={twJoin(
                  "flex min-w-0 flex-1 p-1.5 font-inter-normal-12px text-gray-800",
                  rightAlignValues && "justify-end",
                )}
                data-loc={`${locPrefix}-property-value-${key}`}
              >
                {value}
              </div>
            </div>
          ))}
    </div>
  );
};
